import { zodResolver } from '@hookform/resolvers/zod'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Alert, Box, Button, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, Link as RouterLink, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { ApiError, api, setCsrf } from '../api/client'
import { keys, useMe } from '../api/hooks'
import type { User } from '../api/types'
import { SenderAvatar } from '../lib/Avatar'
import { Wordmark } from '../lib/Wordmark'

const schema = z.object({
  email: z.string().trim().min(1, 'Enter your email address').email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  totp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code').optional().or(z.literal('')),
})
type Form = z.infer<typeof schema>

const PANEL = '#06372c'
const GOLD = '#f0b94d'

const PREVIEW: Array<{ name: string; subject: string; time: string; unread?: boolean }> = [
  { name: 'Mohammed Al-Khalidi', subject: 'Q3 partnership proposal ready for review', time: '10:42', unread: true },
  { name: 'Sarah Rashid', subject: 'Contract documents are in the shared folder', time: '09:15', unread: true },
  { name: 'Finance', subject: 'Backup completed successfully', time: '06:00' },
  { name: 'Layla Haddad', subject: 'Re: Onboarding schedule for next week', time: 'Sep 20' },
  { name: 'Orbimail', subject: 'Your workspace is ready', time: 'Sep 18' },
]

/** Static product preview (not interactive): shows what is behind the sign-in, like other product sign-in pages. */
export function ProductPanel() {
  return (
    <Box component="aside" aria-hidden sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', bgcolor: PANEL, color: '#fff', p: { md: 6, lg: 8 }, overflow: 'hidden', position: 'relative' }}>
      {/* Orbit rings: the brand motif from the name. Flat strokes, no gradient. */}
      <Box component="svg" viewBox="0 0 800 800" aria-hidden sx={{ position: 'absolute', right: -220, top: -120, width: 820, height: 820, pointerEvents: 'none' }}>
        {[380, 290, 200].map((r) => <circle key={r} cx="400" cy="400" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="1.5" />)}
        <circle cx="150" cy="560" r="9" fill={GOLD} />
        <circle cx="633" cy="640" r="6" fill="rgba(255,255,255,.55)" />
        <circle cx="600" cy="130" r="5" fill="rgba(255,255,255,.4)" />
      </Box>
      <Typography component="p" sx={{ position: 'relative', fontSize: { md: 32, lg: 40 }, lineHeight: 1.12, fontWeight: 600, letterSpacing: '-0.025em', maxWidth: 520, textWrap: 'balance' }}>
        Your email. Your files. Your orbit.
      </Typography>
      <Typography sx={{ position: 'relative', mt: 2, maxWidth: 440, color: 'rgba(255,255,255,.75)', fontSize: 16, lineHeight: 1.55 }}>
        Private email, calendar and contacts for your team, hosted in the EU.
      </Typography>

      <Box sx={{ position: 'relative', mt: 'auto', pt: 6, mr: { md: -14, lg: -20 }, mb: { md: -14, lg: -20 } }}>
        <Box sx={{ position: 'absolute', top: 6, right: { md: 120, lg: 200 }, zIndex: 2, bgcolor: '#fff', color: '#121316', borderRadius: 3, px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,.28)' }}>
          <SenderAvatar name="Sarah Rashid" size={32} />
          <Box><Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>Sarah Rashid</Typography><Typography sx={{ fontSize: 12, color: '#4a4d52' }}>New message · just now</Typography></Box>
        </Box>
        <Box sx={{ bgcolor: '#fff', color: '#121316', borderRadius: '16px 0 0 0', boxShadow: '0 12px 40px rgba(0,0,0,.3)', pt: 2, pl: 2.5, minHeight: 380 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1.5 }}>Inbox</Typography>
          {PREVIEW.map((m, i) => (
            <Box key={m.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, pr: 3, bgcolor: i === 0 ? '#e4f1ef' : 'transparent', ml: -2.5, pl: 2.5 }}>
              <SenderAvatar name={m.name} size={32} />
              <Typography noWrap sx={{ width: 190, fontSize: 14, fontWeight: m.unread ? 700 : 400 }}>{m.name}</Typography>
              <Typography noWrap sx={{ flex: 1, fontSize: 14, color: '#4a4d52', fontWeight: m.unread ? 600 : 400 }}>{m.subject}</Typography>
              <Typography sx={{ fontSize: 12, color: '#4a4d52', width: 48, textAlign: 'right' }}>{m.time}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export function LoginPage() {
  const me = useMe()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [needTotp, setNeedTotp] = useState(false)
  const [show, setShow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Form>({ resolver: zodResolver(schema) })

  if (me.data) return <Navigate to={me.data.setupRequired ? '/setup' : '/'} replace />

  const onSubmit = handleSubmit(async (v) => {
    setError(null)
    try {
      const r = await api<{ user: User; csrf: string }>('/auth/login', { method: 'POST', body: { email: v.email.trim(), password: v.password, totp: v.totp || undefined } })
      setCsrf(r.csrf)
      qc.setQueryData(keys.me, r.user)
      navigate(r.user.setupRequired ? '/setup' : '/', { replace: true })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TOTP_REQUIRED') { setNeedTotp(true); return }
      if (e instanceof ApiError && e.status === 429) setError(`Too many attempts. Wait ${Math.max(1, Math.ceil((e.body?.retryAfter ?? 60) / 60))} minute(s), then try again.`)
      else if (e instanceof ApiError && e.status === 401) setError(e.code === 'TOTP_INVALID' ? 'That verification code is not valid.' : 'Incorrect email or password.')
      else if (e instanceof ApiError && e.status === 400) setError('Enter your full email address and password.')
      else setError('Could not reach the mail server. Try again in a moment.')
    }
  })

  return (
    <Box sx={{ height: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, bgcolor: 'background.default' }}>
      <Box component="main" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto', px: { xs: 3, sm: 6, lg: 12 }, py: { xs: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#fff', display: 'grid', placeItems: 'center' }}><Box component="img" src="/logo.png" alt="" sx={{ width: 30, height: 30 }} /></Box>
          <Wordmark size={24} />
        </Box>

        <Box component="form" onSubmit={onSubmit} noValidate sx={{ width: '100%', maxWidth: 440, my: 'auto', py: 6, display: 'grid', gap: 2.5 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: 32, md: 40 }, fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.1 }}>Welcome back</Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: 16 }}>Sign in to your Orbimail mailbox.</Typography>
          </Box>

          {error && <Alert severity="error" role="alert">{error}</Alert>}

          <TextField label="Email address" type="email" autoComplete="username" autoFocus fullWidth
            error={!!errors.email} helperText={errors.email?.message} slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } }} {...register('email')} />
          <TextField label="Password" type={show ? 'text' : 'password'} autoComplete="current-password" fullWidth error={!!errors.password} helperText={errors.password?.message}
            slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((v) => !v)} edge="end" size="small">{show ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> } }}
            {...register('password')} />
          {needTotp && <TextField label="Verification code" autoComplete="one-time-code" autoFocus fullWidth error={!!errors.totp} helperText={errors.totp?.message ?? 'From your authenticator app.'} slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }} {...register('totp')} />}

          <Button type="submit" variant="contained" size="large" disabled={isSubmitting} disableElevation sx={{ height: 52, fontSize: 16, fontWeight: 600 }}>
            {isSubmitting ? 'Signing in…' : needTotp ? 'Verify' : 'Continue'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/forgot-password" underline="hover">Forgot your password?</Link> Need an account? Ask your Orbimail administrator.
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary"><Link href="https://orbimail.net" target="_blank" rel="noopener noreferrer" underline="hover">orbimail.net</Link></Typography>
      </Box>
      <ProductPanel />
    </Box>
  )
}
